import * as React from "react";
import { cn } from "@/shared/lib/utils";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/shared/ui/components/ui/radio-group";

export interface RadioProps {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  label?: string;
  helper?: string;
  error?: boolean;
  variant?: "default" | "advanced";
  size?: "sm" | "xl";
  value?: string;
  name?: string;
  contentClassName?: string;
  contentHelper?: string;
}

const RadioButton = React.forwardRef<
  React.ElementRef<typeof RadioGroupItem>,
  RadioProps
>(
  (
    {
      checked = false,
      onCheckedChange,
      disabled,
      className,
      label,
      helper,
      error,
      variant = "default",
      size = "sm",
      value = "radio",
      name,
      contentClassName,
      contentHelper,
    },
    ref,
  ) => {
    const hasError = Boolean(error);
    const [internalValue, setInternalValue] = React.useState(
      checked ? value : "",
    );

    React.useEffect(() => {
      setInternalValue(checked ? value : "");
    }, [checked, value]);

    const defaultRadioStyles = cn(
      size == "xl" ? "h-4 sm:h-5 w-4 sm:w-6" : "h-4 w-4 ",
      "rounded-full border",
      "!border-grey-200",
      "bg-white",
      "hover:!border-grey-500 disabled:hover:!border-grey-100",
      "data-[state=checked]:!border-brand-green data-[state=checked]:text-brand-green",
      "[&_[data-state=checked]_svg]:fill-brand-green",
      "disabled:bg-grey-50 disabled:!border-grey-100",
      "disabled:data-[state=checked]:bg-green-50 disabled:data-[state=checked]:!border-green-300 disabled:data-[state=checked]:text-green-300",
      "disabled:[&_[data-state=checked]_svg]:fill-green-300",
      hasError &&
        !disabled &&
        !checked &&
        "!border-error-700 hover:!border-error-700",
    );

    const advancedContainerStyles = cn(
      "flex items-center gap-2 p-3 border",
      "rtl:flex-row-reverse",
      size === "sm" && "rounded-lg",
      size === "xl" && "rounded-lg sm:rounded-xl",
      "bg-white border-grey-200",
      !disabled && !checked && "hover:bg-grey-100",
      checked && "bg-green-100 border-brand-green",
      "disabled:data-[checked=true]:bg-green-50 disabled:data-[checked=true]:border-green-100 disabled:data-[checked=true]:hover:bg-green-50 disabled:data-[checked=true]:hover:border-green-100",
      "disabled:data-[checked=false]:bg-grey-50 disabled:data-[checked=false]:border-grey-100 disabled:hover:bg-grey-100",
      hasError && !disabled && !checked && "!bg-error-bg !border-error-700",
    );

    const advancedRadioStyles = defaultRadioStyles;

    const handleValueChange = (newValue: string) => {
      if (disabled) return;
      const isChecked = newValue === value;
      if (isChecked !== checked) {
        onCheckedChange?.(isChecked);
      }
    };

    const handleClick = (e: React.MouseEvent) => {
      if (disabled) return;
      if (checked) {
        e.preventDefault();
        e.stopPropagation();
        setInternalValue("");
        onCheckedChange?.(false);
      }
    };

    if (variant === "advanced") {
      return (
        <div className={cn("inline-flex", className)}>
          <RadioGroup
            value={internalValue}
            onValueChange={handleValueChange}
            disabled={disabled}
            // className="inline-flex"
          >
            <label
              className={cn(
                advancedContainerStyles,
                contentClassName,
                "cursor-pointer gap-2 items-start",
                disabled && "cursor-not-allowed",
              )}
              data-checked={checked}
              data-error={hasError || undefined}
              onClick={handleClick}
            >
              <RadioGroupItem
                ref={ref}
                value={value}
                disabled={disabled}
                className={advancedRadioStyles}
                data-error={hasError || undefined}
              />
              {label && (
                <div className="flex flex-col gap-3 items-start  rtl:items-end ">
                  <span
                    className={cn(
                      "text-sm font-medium",
                      disabled ? "text-grey-400" : "text-grey-900",
                    )}
                  >
                    {label}
                  </span>
                  {contentHelper && (
                    <p className="text-xs text-grey-300 font-medium rtl:text-right">
                      {contentHelper}
                    </p>
                  )}
                </div>
              )}
            </label>
          </RadioGroup>
        </div>
      );
    }

    return (
      <div className={cn("flex items-center gap-2", className)}>
        <RadioGroup
          value={checked ? value : ""}
          onValueChange={handleValueChange}
          disabled={disabled}
          // className="inline-flex"
        >
          <RadioGroupItem
            ref={ref}
            value={value}
            disabled={disabled}
            className={defaultRadioStyles}
            data-error={hasError || undefined}
            onClick={handleClick}
          />
        </RadioGroup>
        {(label || helper) && (
          <div className="flex flex-col mb-[1px] gap-2">
            {label && (
              <label
                className={cn(
                  "text-xxs cursor-pointer",
                  size === "sm" ? "font-medium" : "font-bold",
                  hasError && !disabled && !checked && "text-error-700",
                  disabled ? "text-grey-200" : !hasError && "text-black",
                )}
                onClick={() => !disabled && onCheckedChange?.(!checked)}
              >
                {label}
              </label>
            )}
            {helper && (
              <p
                className={cn(
                  "text-xxs",
                  size === "sm" ? "font-medium" : "font-bold",
                  disabled ? "text-grey-200" : !hasError && "text-grey-400",
                )}
              >
                {helper}
              </p>
            )}
          </div>
        )}
      </div>
    );
  },
);

RadioButton.displayName = "RadioButton";

export { RadioButton };
